import type { HelpTopic } from '../core/index.js'

/** The schedule module's help topics (docs/plans/in-app-help-plan.md). */
export const SCHEDULE_HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: 'schedule.publish-shifts',
    title: 'Publish shifts',
    seats: ['operator', 'facility_administrator', 'admin'],
    summary: 'Opens the shifts workers bid for and the month is built from.',
    sections: [
      {
        heading: 'What it publishes',
        points: [
          'Every date from the first to the last, on each unit you choose (the facility\'s units from Configuration › Details, or ones you name where it keeps no list), of each shift type you choose.',
          'Each shift type runs at this facility’s hours, set on Configuration under the Scheduling tab.',
          'Seats per shift opens that many places on each shift, one worker a seat. Role keeps the shift to one role; Any role leaves it open to all.',
          'At most 92 days at a time, from today on.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'The form starts on the first month ahead with nothing published, on the units already in use.',
          'Publishing dates again is safe: a seat a worker holds is kept, and the others take the new hours.',
          'A month can be bid for once its shifts are published, and built from what workers ask for.',
        ],
      },
    ],
    related: ['schedule.build-the-month'],
  },
  {
    id: 'schedule.heatmap',
    title: 'The facility at a glance',
    seats: ['operator', 'facility_administrator'],
    summary: 'One colour for how well the facility is staffed: each shift, each day and each month, from the seats the census requires against the seats someone holds.',
    sections: [
      {
        heading: 'What a seat is',
        points: ['A seat is not a bed. Beds hold residents and the census counts them; the staffing plan turns the census into seats - one worker of a role, on one unit, for one shift.'],
      },
      {
        heading: 'The colours',
        points: [
          'Covered: every seat required is held, or was worked.',
          'Nearly covered: a single seat is missing (on a shift, one the board is still offering), or at least 96.5% of a large day’s seats are held.',
          'Thin: a few seats short (89% or more held) - worth watching, not yet urgent.',
          'Short: seats ran short, or an open seat is inside the lock or not on the board.',
          'Critical: well under the plan - under 80% of the seats, or a role with nobody on it.',
          'A day or month is read from the share of its seats held: all of them is Covered, a single seat missing or 96.5% or more Nearly covered, 89% or more Thin, 80% or more Short, and less than that Critical. A unit or role chosen with the filter is read the same way.',
        ],
      },
      {
        heading: 'Time',
        points: [
          'Solid: worked, or under way now. Striped: still ahead, so it can change as seats are taken. Grey: a month not released.',
          'A past day counts what its board held when it was worked, so a call-out covered from the open board stays Covered; a day ahead counts what the board holds now.',
          'The hour-by-hour strip on Today shows the seats on duty each hour against what the shifts covering it require, with a marker at now. A day chosen in the week strip has no hour strip: every cell is striped, still ahead.',
          'A shift that is over says “worked in full” or how many “went unfilled”; “over” on a cell means one more seat held than required, never that the shift has ended.',
          'On the Month tab, tap a day for its units by shift, and a row of the last twelve months to open that month.',
        ],
      },
    ],
    related: ['schedule.today'],
  },
  {
    id: 'schedule.today',
    title: 'Today',
    seats: ['operator', 'facility_administrator'],
    summary: 'The facility’s day in one place: what needs you, today’s census and coverage, the open shifts and what the engine did on its own.',
    sections: [
      {
        heading: 'What the engine does alone',
        points: [
          'It builds the month when bidding closes and releases it after the review window, unless something blocks it or you hold it.',
          'When you record a census it compares the day with the staffing plan: a fall releases shifts lowest tier first, a rise offers held seats back and posts new ones.',
          'Open shifts - seats nobody holds for an unplanned reason - go only to workers who are eligible: the right role, unit and credentials, free that day and under the hours rules. First come (the default) every eligible worker may take a seat from the day it is posted and the first to accept gets it; in the facility’s order they are offered one at a time first. As the shift nears the engine tells everyone at once, repeats itself, opens a Clinical Bridge callout where Bridge is on, and in the last hours brings the seat to Needs you. It moves whether or not this screen is open.',
        ],
      },
      {
        heading: 'Reading a seat’s life',
        points: [
          'Open a seat on Open shifts in flight and its life is a timeline: born, posted, nudged, urgent, told again, the Clinical Bridge callout, the lock, and how it ended. Under each step the engine says why in its own words - what triggered the move, the setting behind it as the Automation tab names it with its interval, and what it means - and then what it will do next and when.',
          'The birth says why the seat exists at all: who called out and who reported it, the census count and what the plan needs at it, the run that left it open, or that it is a demo seat on its own clock. What the engine did carries the same account beside each line.',
        ],
      },
      {
        heading: 'Needs you',
        points: [
          'Only what the engine may not decide alone: a shift starting within the facility’s last hours (4 by default) with seats nobody took - or whose Bridge callout expired unanswered - as one item however many seats (first come it says who was told and how many could still take it, and offers a callout or run short; in the facility’s order it names who could take it and assigns one at a time), a census change too close to the shift, a swap the rules held, a month its health blocks, and a unit with no count today. A shift that started with nobody in its seat is a reminder for a few days: record it ran short, or leave it.',
          'Each item says why the engine stopped and has the buttons that settle it. Urgent items come first.',
          'A seat the census requires this week that nobody holds and the board is not offering is an item too, so The day at a glance never shows a short shift you are not asked about: post it to the board (the engine then offers it like any open shift), open a callout, or record the census when the requirement is read from a forecast - the engine acts only on a recorded count.',
          'Running a seat short asks for a reason code; it is recorded on the schedule and in the activity feed.',
          'Where many approvals are routine - swaps held only because the two are in different tiers - one button approves them all.',
        ],
      },
      {
        heading: 'The day at a glance',
        points: [
          'The day’s coverage in one place: every unit, role and shift against what the census requires, the day’s verdict on one scale, and the hours with a marker at now. The Day tile above it is the same verdict in a word.',
          'Beside the grid are the open shifts in flight - the seats the engine is offering, blasting first come, holding for recall or has left to you - so what is short and what is being done about it read together.',
          'Pick a day in the week strip under Census and the section redraws for that day - striped throughout, since it is still ahead - with Back to today and a link to census planning. The outlined day in the strip is the one drawn.',
        ],
      },
      {
        heading: 'A new facility',
        points: ['While there is nothing to run, Needs you is replaced by the set-up steps in the order they unlock each other: units, the staffing plan, workers, the month’s shifts and the first census.'],
      },
      {
        heading: 'A large facility',
        points: ['Choose a unit or a role at the top and every section narrows to it. The choice is remembered in this browser.'],
      },
    ],
    related: ['schedule.census', 'schedule.build-the-month'],
  },
  {
    id: 'schedule.month-trends',
    title: 'Trends',
    seats: ['operator', 'facility_administrator'],
    summary: 'How many residents were in beds each day of the month, against the census the month’s shifts were published for.',
    sections: [
      {
        heading: 'Reading the line',
        points: [
          'Facility adds every unit together; pick a unit to see it alone. A day the facility total leaves blank is one a unit has no count for.',
          'Solid is a count staff recorded. Dashed is the forecast: the same weekday’s average over the last few weeks, or the unit’s planned census when there are no recent counts.',
          'The grey dashed line is the planned census: what the month’s shifts were published for. Days well under it are days the census response may flex shifts; days over it, days it may add seats.',
          'Hover or tab to a day for its count, where it came from and how far it is from planned. Show as a table lists every day.',
        ],
      },
      {
        heading: 'The figures above it',
        points: [
          'Average, low and high are over the days with a count. Patient days are the month’s counts added up: one resident in a bed for one day is one patient day.',
          'Record a day’s count on the Today tab, or in Census planning for any day.',
        ],
      },
    ],
    related: ['schedule.census'],
  },
  {
    id: 'schedule.census',
    title: 'Census',
    seats: ['operator', 'facility_administrator'],
    summary: 'Record how many residents each unit has today, see what that requires at your staffing target, and try a different count on any day.',
    sections: [
      {
        heading: 'The words',
        points: [
          'PPD is patient days: one resident in a bed for one day. A day’s census is that day’s PPD, and a month’s PPD is its days added up.',
          'HPPD is hours per patient day: nursing hours worked divided by PPD. It is the staffing ratio facilities plan and report on.',
          'Your staffing plan, under Configuration › Scheduling, says the HPPD each role should reach and how the day’s hours split across shifts. From a census it gives the seats each shift needs.',
        ],
      },
      {
        heading: 'Each morning',
        points: [
          'Each unit’s box sits beside its name and starts at today’s forecast, marked Not recorded yet. Correct it to the real count and press Record; the box then says Recorded. The midnight census is the usual count.',
          'What today requires shows, per unit, the planned HPPD against the target and the seats by role on each shift.',
          'The count is capped at the unit’s beds. A unit with no beds set takes any count.',
          'A facility that lists no units has nowhere to record a count: Set up the units opens Configuration › Details to list them first.',
        ],
      },
      {
        heading: 'The weeks ahead',
        points: [
          'A date with nothing recorded shows a forecast in grey: the same weekday’s average over the last few weeks, else the unit’s planned census.',
          'The grid shows the weeks around today; pick a month to reach a released month further out. Tap any cell to try a different count. The What-if works out what it would require and, for a released month, what would change: who would be moved to an open seat or released - the lowest reliability tier first, so a higher tier keeps what it bid for - what seats would post and who would be offered them, and where a minimum staffing line holds. Record keeps the count as the expected census for that day.',
          'A connected EHR writes the same counts through its feed; a count you record replaces its figure for that day.',
          'Recording a count for a released month makes the engine act. When the census falls it releases shifts from the lowest reliability tier first - moving anyone an open seat on another unit can take, flexing the rest and holding their seat with first right back - and tells each worker. Where the facility applies by hand, or the shift starts too soon (inside the notice hours or the lock hours), the change waits under What the engine did for you to apply or dismiss.',
          'When the census rises, a worker whose seat was held is offered it back first; what is still short is posted on the open board, where it is offered down the facility’s add order (PRN then agency unless set otherwise) and then everyone first come. Where the plan rounds into short shifts, a short seat posts at the short shift’s hours. A held seat can also be offered back by hand from the same list, and one whose recall runs out goes to the board on the hour.',
        ],
      },
    ],
    related: ['facilities.scheduling-census', 'facilities.scheduling-census-response', 'schedule.build-the-month'],
  },
  {
    id: 'schedule.workforce-plan',
    title: 'Workforce',
    seats: ['operator', 'facility_administrator'],
    summary: 'How many people the staffing plan needs on the books, and the lines each shift needs on an every-other-weekend rotation.',
    sections: [
      {
        heading: 'The numbers',
        points: [
          'Each unit is planned on its planned census, set under Configuration › Details, or else the average of the counts recorded over the last few weeks. A unit with neither is left out.',
          'Hours a day are what the staffing plan schedules at that census, short shifts included; the target the HPPD asks for is beside them.',
          'FTEs needed are the hours a day × 7 over an FTE’s week, 40 hours unless set otherwise. The buffer, 12% unless set otherwise, covers PTO, call-offs and turnover.',
          'The roster counts a full-timer as one FTE, or their contracted hours; a part-timer by their contracted hours, or else the middle of their tier’s part-time hours. PRN and agency staff are your flexible pool and count by head, not as FTEs.',
          'Hires is the gap rounded up, in full-timers. The same hours can be part-timers.',
        ],
      },
      {
        heading: 'The lines',
        points: [
          'For each unit, shift and role, the lines are the repeating positions a two-week rotation needs: every full-timer works five shifts a week on 8-hour shifts and every other weekend, so half of them work each weekend.',
          'Weekends come up short that way. The weekend-only lines are the positions that work every Saturday and Sunday to fill it - usually part-time staff.',
          'Held by names the full-timers whose home unit and usual shift (the one they worked most last released month) the line is. Keeping people on the same unit and shift helps care and retention.',
          'Show the pattern lays the two weeks out line by line, with how many are on each day.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'It is a plan to hire and build lines from. It never changes a month: workers still bid, and the engine builds the month from their preferences.',
          'Staff usually want the schedule 2 to 4 weeks ahead. The card says so when the release is set later than two weeks before the month.',
        ],
      },
    ],
    related: ['schedule.staffing-report', 'facilities.scheduling-census'],
  },
  {
    id: 'schedule.staffing-report',
    title: 'Staffing report',
    seats: ['operator', 'facility_administrator'],
    summary: 'Hours per patient day, scheduled and worked, against your target, by unit and day.',
    sections: [
      {
        heading: 'The figures',
        points: [
          'PPD is patient days: a day’s recorded census, summed over the days that have one. A forecast never counts.',
          'Scheduled HPPD is the hours on the released month, as it now stands after swaps, pickups and low-census releases, over the PPD.',
          'Worked HPPD is the hours of completed shifts the partner reported, over the PPD. It is what PBJ and Care Compare report.',
          'Agency hours are part of the hours worked and shown apart; released hours are the seats the census took back.',
        ],
      },
      {
        heading: 'Reading it',
        points: [
          'The month’s HPPD uses only the days with a census, so the ratio compares like with like. A day without a count shows its hours and no HPPD.',
          'The target is the HPPD your staffing plan sets under Configuration › Scheduling; a grid plan has no single target.',
          'Weekends are shown apart, as Care Compare reports them publicly: it is where most buildings drop. Each day in the table also gives its worked HPPD by role.',
        ],
      },
    ],
    related: ['schedule.census', 'facilities.scheduling-census'],
  },
  {
    id: 'schedule.build-the-month',
    title: 'Build the month',
    seats: ['operator', 'facility_administrator'],
    summary: 'Turns every worker’s preferences into the month’s schedule, settles who gets a shift two people want, and sends each worker their part when you release it.',
    sections: [
      {
        heading: 'How a month comes together',
        points: [
          'Workers save their preferences in their own app: the shift types they will work, their hours, days off and PTO.',
          'Preferences close on the date the timeline shows. After it, nobody can save or change preferences for the month.',
          'Build the month drafts everyone at once under this facility’s scheduling rules.',
          'When two workers want the same shift, the higher tier gets it, then the higher score, then the longer service.',
          'Probationary workers do not bid. The month is assigned to them.',
          'A seat a minimum staffing line is still short on takes a bidder’s day off, where the Scheduling tab says so: Low days first, then Medium, then High, and the lowest-ranked bidder off that day. The result names it, and so does the worker’s released month.',
        ],
      },
      {
        heading: 'Reading the result',
        points: [
          'Each worker’s line shows what they asked for, what they got and their hours, and names who got any shift they lost.',
          'Contested shifts lists every shift type on a day that more workers wanted than it had seats, counting every unit, and why each seat went where it did. A worker given the same shift type that day on another unit has not lost it.',
          'Unfilled shifts go to the open board once the month is released.',
          'Where the facility keeps minimum staffing, the dates that fall short are listed too.',
        ],
      },
      {
        heading: 'What you can do here',
        points: [
          'Where the facility builds the month at the close, the engine builds it when preferences close and the run says so. Look it over and release it.',
          'Build as often as you like before releasing. Every build is kept.',
          'Assign seats opens the unfilled list: each seat takes a worker by hand before the release, from everyone the hard rules admit - the role, the unit, the credentials, not already on a shift that day. A placement that passes a month rule (a day after an evening, days in a row, the week’s cap, overtime) asks for a reason code the run keeps. Build again and the assignments start over.',
          'Release to workers sends each worker their schedule. A month is released once, and nothing reaches workers before.',
          'After the release, the card below handles changes, swaps and open shifts.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'The rules the build follows are on Configuration, under the Scheduling tab.',
          'A worker who bids and saved no preferences for the month is drafted from last month’s preferences, without its PTO or days off. With none last month either, they are assigned last, from the shifts everyone else left. The result marks both, and the worker is told which.',
        ],
      },
    ],
    related: ['schedule.month-readiness', 'roster.dashboard'],
  },
  {
    id: 'schedule.month-readiness',
    title: 'Is the month ready to build?',
    seats: ['operator', 'facility_administrator'],
    summary: 'The four counts and the timeline at the top of Build the month, and the detail behind each count.',
    sections: [
      {
        heading: 'The counts',
        points: [
          'On the roster: everyone working here during the month, split into those who bid and those whose month is assigned.',
          'Preferences saved: out of the workers who bid. An assigned tier saves none, so they are not counted here.',
          'Drafts submitted: out of everyone on the roster. Assigned workers submit a draft too, built from the rules.',
          'Shifts published: every seat in the month, and how many days have nothing published.',
          'A gold dot is something you can still act on: workers who have not saved preferences while the month takes them. Remind them from the table.',
          'A coral dot is something that will change the build: workers who missed the close get last month’s preferences or are assigned last, and days with nothing published cannot be filled.',
          'No dot means nothing to act on. Drafts are a count only: the build works from saved preferences.',
          'The tile with a dark border is the one whose detail is showing below.',
        ],
      },
      {
        heading: 'Opening a count',
        points: [
          'Tap a count to see who is behind it. The table lists everyone in the order the build drafts them: tier, then score, then seniority.',
          'Use the filters to find who has not saved preferences, who has not submitted, or whose draft is out of date.',
          'Tap a worker to read their preferences for the month.',
          'Remind sends a worker who bids and has not saved preferences one message: while the month takes them, asking for them; once they have closed, telling the worker that without preferences they are assigned their previous month’s preferences or the remaining shifts. The To remind filter lists everyone it applies to.',
          'Remind all, above the table, sends one to everyone on that list who has not had one today, after you confirm the number.',
          'A worker can be reminded about a month once a day. The row shows when the last reminder went.',
          'Assigned workers, probationary by default, are never reminded: the build assigns their month from the rules.',
          'Shifts published opens the month as a calendar, with the seats each shift type has beside how many workers asked for it.',
          'In the calendar a solid square is a filled seat and an outlined coral square an open one. Seats are filled from the released month, or from the latest run until you release it. Tap a day to see who holds each seat.',
        ],
      },
      {
        heading: 'The timeline',
        points: [
          'Preferences close, the draft locks, the month is released and the change window closes, in that order, in the facility’s time zone.',
          'The next step is marked in coral with how long until it comes.',
          'After preferences close, a worker who saved none is drafted from last month’s preferences, or assigned last from the shifts left. Remind tells them so.',
        ],
      },
    ],
    related: ['schedule.build-the-month'],
  },
  {
    id: 'schedule.worker-shifts',
    title: 'Your shifts',
    seats: ['worker'],
    summary: 'Your schedule in one place: what you ask for next month, the month your facility released, and a calendar of your shifts.',
    sections: [
      {
        heading: 'What you see, top to bottom',
        points: [
          'The bidding card: tell your scheduler what you want for next month, then build and submit your schedule. It opens once your facility has published that month’s shifts.',
          'The calendar. A released month is headed by its shifts and hours as they stand now, and followed by what changed since the release. Clinical Bridge is underneath.',
          'Your shifts now, with swaps, changes and the open shifts you can take. First come, every open seat you are eligible for is yours to take from the day it is posted - Take it, and the first to accept gets it - and you get one daily digest of the seats newly open to you, then an urgent notice as the shift nears. A seat starting within hours can still be taken until it starts.',
          'Past preferences: what you asked for in earlier months, to read back. They can no longer be changed.',
        ],
      },
      {
        heading: 'Reading the calendar',
        points: [
          'The fill says whose the day is. Solid blue is your shift, with its type. Green is a shift you worked, light coral a call-out, coral a no-show. Gold is PTO (a dashed edge while requested) or a holiday event.',
          'A small mark on the corner says how a shift came to you after the release: + picked up, a person assigned by your supervisor, two arrows swapped in, an arrow moved from another shift, a circling arrow taken back after low census, a lightning bolt a Clinical Bridge callout you took. A coral ! means it was worked against what you asked: a PTO day denied or a day off taken to cover. A day as released has no mark.',
          'A coral outline with a dot is a shift offered to you, waiting for your answer. A thin coral outline with the shift struck through was taken off your schedule (a coral − on the corner); a circling arrow there means it is offered back to you.',
          'Light coral with no shift is a day you asked for that went to someone ranked ahead. A blue outline means open shifts that day; a dashed blue outline with a lightning bolt is a Clinical Bridge day you are on call for.',
          'A letter at the bottom left is the unit, when it is not your home unit. Light blue with a dashed edge is your draft of a month not yet released.',
          'Tap any marked day for its details: what happened, when, and why.',
        ],
      },
    ],
    related: ['schedule.worker-preferences', 'schedule.worker-result', 'schedule.worker-board', 'schedule.worker-bridge'],
  },
  {
    id: 'schedule.worker-preferences',
    title: 'Telling your scheduler what you want',
    seats: ['worker'],
    summary: 'Five short steps that tell your facility how you would like to work next month. Your schedule is built from them.',
    sections: [
      {
        heading: 'The steps',
        points: [
          'Shift preference: put the shift types in the order you prefer them, and mark any you will never do.',
          'Hours target: the range of hours you want. Your facility sets the lowest and highest for your tier and how you are employed.',
          'PTO: the days you want off as paid time off, counted against your balance and what your tier allows.',
          'Days off: tap a day for High, again for Medium, again for Low. High days are avoided at all costs; Medium and Low are kept where they can be. Part-time and PRN staff see Days you can work instead: they mark the days they can work, and every other day is left alone.',
          'Build: check what you asked for, then build your schedule.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'Where your facility runs a bid window, bidding opens on a set day and time and runs for a few days. You get a message when it opens, and Shifts says when it opens and closes.',
          'You can come back and change your preferences until they close. The date is shown at the top of the steps.',
          'Once preferences close, or your facility releases the month’s schedule, they can no longer be saved or changed for that month. You can still read them under Past preferences on Shifts.',
          'Your preferences ask; they do not promise. When two people want the same shift, the higher tier gets it, then the higher score.',
          'When a seat your unit must staff is still short after everyone is drafted, someone off that day covers it: Low days go first, then Medium, then High, and the lowest-ranked bidder off that day is the one. A higher reliability score protects your days off.',
        ],
      },
    ],
    related: ['schedule.worker-draft'],
  },
  {
    id: 'schedule.worker-draft',
    title: 'Your draft schedule',
    seats: ['worker'],
    summary: 'What your preferences would give you this month, built by your facility’s rules, for you to check and submit.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'The month with the shift type on each working day, and your hours.',
          'Notes where something did not fit: a day off that had to be used, a target not reached, or a rule your tier follows.',
          'Your days off and PTO, marked on the days you asked for them.',
          'Each PTO day and whether it is approved, denied or still to be decided.',
        ],
      },
      {
        heading: 'What you can do here',
        points: ['Submit as-is sends it to your scheduler.', 'Make changes takes you back to your preferences. Build again once you have changed them.'],
      },
      {
        heading: 'Good to know',
        points: [
          'A draft does not claim any shift. Your scheduler builds the whole month from everyone’s drafts.',
          'For the month you are already in, the draft starts today. Your minimum is scaled to the days left, and days gone by are marked past.',
          'If your preferences, your facility’s rules or its published shifts change, the draft says so. Build it again to match.',
        ],
      },
    ],
    related: ['schedule.worker-preferences', 'schedule.worker-result'],
  },
  {
    id: 'schedule.worker-assigned',
    title: 'Your assigned month',
    seats: ['worker'],
    summary: 'In your tier your facility assigns your schedule instead of asking for preferences.',
    sections: [
      {
        heading: 'How it is assigned',
        points: [
          'Every weekend first, then weekdays, with the usual shift for each day, up to the hours your tier works.',
          'Build my schedule shows you the month. Check it, then submit it.',
        ],
      },
      {
        heading: 'Good to know',
        points: ['If your tier changes to one that bids when a month closes, you tell your scheduler your own preferences from then on.'],
      },
    ],
    related: ['scoring.worker-home'],
  },
  {
    id: 'schedule.worker-result',
    title: 'Your released month',
    seats: ['worker'],
    summary: 'The schedule your facility built and released for the month: your shifts, your hours, and what happened to what you asked for.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'Your shifts and hours as they stand now, with every change since the release, and when the month was released.',
          'Since the release: each shift picked up, assigned by your supervisor, swapped, moved or taken off, by day.',
          'Days you asked for that went to someone ranked ahead, light coral on the calendar.',
          'Each PTO day and whether it was approved or denied. A denied day is a working day.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'Your reliability score is your rank: the higher tier gets a shared shift, then the higher score, then the longer service.',
          'Working a denied PTO day adds points to your score once you work it.',
          'If you didn’t save preferences for the month, last month’s were used, without their PTO or days off. With none last month either, your month was built from the shifts left after everyone who did.',
          'To change something now, use your shifts below: the change window, swaps and open shifts.',
        ],
      },
    ],
    related: ['schedule.worker-board'],
  },
  {
    id: 'schedule.worker-board',
    title: 'Swaps, changes and open shifts',
    seats: ['worker'],
    summary: 'How you change the released month: ask for the other shift type, swap with a colleague, or pick up a shift someone needs covered.',
    sections: [
      {
        heading: 'The change window',
        points: [
          'For a short time after the release you can ask for the other shift type on a day you work.',
          'Requests are judged together when the window closes, by reliability score, never first come. A request is granted if a shift of that type is free that day.',
        ],
      },
      {
        heading: 'Swapping a shift',
        points: [
          'Swap lists the colleagues of your role who could take the shift. The person you choose is asked first.',
          'If they decline or do not answer in time, the next on the list is asked, without you seeing who.',
          'Some swaps wait for your manager: different tiers, another unit, or too many shifts in a week.',
          'You can undo a completed swap until shortly before the shift. A swap never changes your score.',
        ],
      },
      {
        heading: 'Open shifts',
        points: [
          'A shift offered to you is yours if you accept it in time. Then it passes on.',
          'In the last 24 hours a shift goes to everyone at once, and the first to accept gets it. Taking one and working it adds points to your score.',
          'Close to the start nothing changes without your supervisor.',
        ],
      },
      {
        heading: 'Low census',
        points: [
          'When your unit’s census falls, shifts are released from the lowest reliability tier first, so a higher tier keeps what it bid for. Volunteers go before anyone: mark the days you would happily take off.',
          'A released shift is not a call-out and your score is not touched. You have first right to it back if the census returns within the recall hours; a coral card asks you to take it back or let it go.',
          'Where an open seat on another unit fits, you are moved there instead of released, same hours.',
        ],
      },
      {
        heading: 'Good to know',
        points: ['Anything waiting for your answer is at the top, under Waiting for your answer.'],
      },
    ],
    related: ['schedule.worker-result', 'messages.worker-contacts'],
  },
  {
    id: 'schedule.worker-bridge',
    title: 'Clinical Bridge',
    seats: ['worker'],
    summary: 'Days you would be willing to come in at short notice if a shift needs covering.',
    sections: [
      {
        heading: 'What you can do here',
        points: [
          'Tap a date on the calendar and Enroll for this day. You can withdraw the same way.',
          'When a shift opens on a day you enrolled for, everyone enrolled is told at once. The first to accept gets it.',
        ],
      },
      {
        heading: 'Good to know',
        points: [
          'There is no penalty for declining or for not answering.',
          'Points come only when you work the shift. Accepting alone changes nothing.',
          'Withdrawing very close to the day goes on your record and takes points off your score.',
          'Your facility decides who may enroll, how many days a month, and how close to the day you may still withdraw. The panel under the calendar says what applies to you.',
        ],
      },
    ],
    related: ['schedule.worker-shifts', 'messages.worker-contacts'],
  },
  {
    id: 'schedule.month-health',
    title: 'The month',
    seats: ['operator', 'facility_administrator', 'admin'],
    summary: 'What is wrong with the month - the census, the workers, the units - and the button that fixes each thing.',
    sections: [
      {
        heading: 'What it reads',
        points: [
          'Census: whether a staffing plan is set, whether each unit has beds or a planned census to forecast from, and the days with no count and nothing to forecast from.',
          'Workers: seats short of what the plan requires on each day’s census - recorded, else forecast - per unit, shift type and role, and seats short of a minimum staffing line, each told apart: published but unfilled (Assign the seats, or the open board once released) or never published (its button publishes exactly those seats in place - the unit, shift type, role and days it names, at the facility’s hours - then build the month again; a seat that does not exist cannot be assigned); seats published above the requirement; a role the roster holds too few full-time equivalents of for the month (the plan’s average hours a day × 7 over 40 hours each, against each worker’s contracted hours, else 1 for full time; PRN and agency are named but not counted, as on the Workforce report); bidders with no preferences; a required credential lapsing; whether the month is built, behind its inputs, or waiting to be released.',
          'Units: no units listed at all (it holds the release once a staffing plan is set, since nothing can be counted or required without them, and names the units the shifts carry), a unit with nothing published, a day with nothing published, shifts on a unit the facility does not list, a unit never counted.',
          'Seats are judged from the released month as the board holds it, else the latest run.',
        ],
      },
      {
        heading: 'The marks',
        points: [
          'Coral holds the release: a minimum staffing line short and a run behind its inputs do by default; Configuration › Scheduling › Timeline and coverage sets what else does and who may release past it.',
          'Gold is a warning: shown, and the month can still be released.',
          'Grey is information - what explains a number, such as seats published above what the plan requires, which is what most of “unfilled” usually is.',
        ],
      },
      {
        heading: 'The buttons',
        points: [
          'Each finding’s button opens the screen that fixes it on the right tab - Census, Publish shifts, Build the month, Assign the seats (the unfilled list, each seat taking a worker by hand before the release), After the release, Configuration - or acts in place: Remind all, Add a worker, a worker’s row.',
          'A facility administrator is told who sets the units and the staffing plan; everything else is theirs to do.',
          'The arrows beside the month step to the month before or after - back to the first month the facility has a schedule or published shifts for, and on to the second month ahead; the Month tab opens on the current month. A month that has ended reads as worked, or as not scheduled here, with nothing left to decide.',
          'The card reads again every minute, so a count recorded in another tab shows.',
        ],
      },
    ],
    related: ['schedule.build-the-month', 'schedule.census', 'schedule.publish-shifts'],
  },
]
